import React, { useEffect, useState, useCallback } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateTimeFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Wallet, LockKeyhole, PlayCircle } from "lucide-react";

export default function Shifts() {
  const { user } = useAuth();
  const { effectiveBranch, isAllBranch } = useBranch();
  const [active, setActive] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [openCash, setOpenCash] = useState("");
  const [closing, setClosing] = useState(false);
  const [actualCash, setActualCash] = useState("");

  const canOperate = ["super_admin", "admin", "branch_head", "cashier"].includes(user.role);
  const branchParam = effectiveBranch ? { branch_id: effectiveBranch } : {};

  const load = useCallback(() => {
    client.get("/shifts/active", { params: branchParam }).then((r) => setActive(r.data)).catch(() => setActive(null));
    client.get("/shifts", { params: branchParam }).then((r) => setHistory(r.data));
    // eslint-disable-next-line
  }, [effectiveBranch]);
  useEffect(() => { load(); }, [load]);

  const openShift = async () => {
    try {
      await client.post("/shifts/open", { branch_id: effectiveBranch || user.branch_id, opening_cash: Number(openCash) || 0 });
      toast.success("Shift dibuka"); setOpenCash(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const closeShift = async () => {
    try {
      await client.post("/shifts/close", { shift_id: active.id, closing_cash_actual: Number(actualCash) || 0 });
      toast.success("Shift ditutup"); setClosing(false); setActualCash(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const expected = active ? (active.opening_cash || 0) + (active.cash_sales || 0) : 0;

  return (
    <div className="p-6 space-y-6 animate-in-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Shift & Kas</h1>
        <p className="text-sm text-muted-foreground">Buka/tutup shift kasir & rekonsiliasi kas</p>
      </div>

      {canOperate && (
        active === null ? (
          <Card className="p-6">
            <div className="flex items-center gap-2 font-heading text-lg font-semibold"><PlayCircle className="h-5 w-5 text-primary" /> Buka Shift Baru</div>
            <div className="mt-4 flex items-end gap-3">
              <div className="space-y-2">
                <Label>Modal Awal (Kas)</Label>
                <Input type="number" value={openCash} onChange={(e) => setOpenCash(e.target.value)} className="h-11 w-56 font-mono" placeholder="0" data-testid="shift-opening-cash" disabled={isAllBranch && !effectiveBranch} />
              </div>
              <Button className="h-11" onClick={openShift} disabled={isAllBranch && !effectiveBranch} data-testid="open-shift-page-btn">Buka Shift</Button>
            </div>
            {isAllBranch && !effectiveBranch && <p className="mt-2 text-xs text-amber-600">Pilih cabang untuk membuka shift.</p>}
          </Card>
        ) : active ? (
          <Card className="border-primary/30 bg-blue-50/50 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <Badge className="bg-emerald-100 text-emerald-700">Shift Aktif</Badge>
                <div className="mt-2 text-sm text-muted-foreground">Dibuka {dateTimeFmt(active.opened_at)} oleh {active.cashier_name}</div>
                <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1 text-sm sm:grid-cols-4">
                  <Stat label="Modal Awal" value={rupiah(active.opening_cash)} />
                  <Stat label="Penjualan Tunai" value={rupiah(active.cash_sales)} />
                  <Stat label="Transaksi" value={active.sales_count} />
                  <Stat label="Kas Seharusnya" value={rupiah(expected)} highlight />
                </div>
              </div>
              <Button variant="destructive" className="h-11" onClick={() => { setClosing(true); setActualCash(String(expected)); }} data-testid="close-shift-btn">
                <LockKeyhole className="mr-2 h-4 w-4" /> Tutup Shift
              </Button>
            </div>
          </Card>
        ) : null
      )}

      <div>
        <h2 className="mb-3 font-heading text-xl font-semibold">Riwayat Shift</h2>
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Kasir</TableHead><TableHead>Buka</TableHead><TableHead>Tutup</TableHead>
              <TableHead className="text-right">Modal</TableHead><TableHead className="text-right">Penjualan</TableHead>
              <TableHead className="text-right">Seharusnya</TableHead><TableHead className="text-right">Aktual</TableHead>
              <TableHead className="text-right">Selisih</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((s) => (
                <TableRow key={s.id} className="hover:bg-slate-50">
                  <TableCell className="text-sm">{s.cashier_name}</TableCell>
                  <TableCell className="text-xs">{dateTimeFmt(s.opened_at)}</TableCell>
                  <TableCell className="text-xs">{s.closed_at ? dateTimeFmt(s.closed_at) : "-"}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(s.opening_cash)}</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(s.cash_sales)}</TableCell>
                  <TableCell className="text-right font-mono">{s.expected_cash != null ? rupiah(s.expected_cash) : "-"}</TableCell>
                  <TableCell className="text-right font-mono">{s.closing_cash_actual != null ? rupiah(s.closing_cash_actual) : "-"}</TableCell>
                  <TableCell className="text-right font-mono">
                    {s.selisih != null ? <span className={s.selisih === 0 ? "text-muted-foreground" : s.selisih < 0 ? "text-rose-600" : "text-emerald-600"}>{rupiah(s.selisih)}</span> : "-"}
                  </TableCell>
                  <TableCell>{s.status === "open" ? <Badge className="bg-emerald-100 text-emerald-700">buka</Badge> : <Badge variant="secondary">tutup</Badge>}</TableCell>
                </TableRow>
              ))}
              {!history.length && <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">Belum ada shift</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={closing} onOpenChange={setClosing}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Tutup Shift</DialogTitle><DialogDescription>Hitung kas fisik di laci dan masukkan jumlahnya.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-100 px-3 py-2 text-sm">Kas seharusnya: <span className="font-mono font-semibold">{rupiah(expected)}</span></div>
            <div className="space-y-2"><Label>Kas Aktual (hasil hitung)</Label>
              <Input type="number" value={actualCash} onChange={(e) => setActualCash(e.target.value)} className="h-12 font-mono text-lg" data-testid="actual-cash" /></div>
            <div className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 text-sm">
              <span>Selisih</span>
              <span className="font-mono font-semibold">{rupiah((Number(actualCash) || 0) - expected)}</span>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setClosing(false)}>Batal</Button><Button onClick={closeShift} data-testid="confirm-close-btn">Tutup & Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-mono font-semibold ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
